import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, TrendingUp } from "lucide-react";
import { formatApproxCAD as fmt, formatApproxNetEstateDisplay } from "@/lib/estate-format";

interface EstateSummaryResult {
  assets: { items: any[]; totalValue: number; probateValue: number; nonProbateValue: number };
  liabilities: { items: any[]; totalValue: number };
  netEstateValue: number;
  monthlyIncome: { items: any[]; total: number };
  monthlyExpenses: { items: any[]; total: number };
  monthlyNet: number;
  eat: { probateValue: number; taxAmount: number; isSmallEstate: boolean; formType: string; note: string };
  completeness: { sectionsWithValues: number; totalAssetSections: number; missingValueSections: string[] };
}

interface Props {
  clientId: string;
}


export function EstateSnapshotCard({ clientId }: Props) {
  const { data: summary, isLoading } = useQuery<EstateSummaryResult>({
    queryKey: ["/api/clients", clientId, "estate-summary"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/estate-summary`);
      if (!res.ok) throw new Error("Failed to fetch estate summary");
      return res.json();
    },
    staleTime: 60_000,
  });

  if (isLoading) return null;

  // 1C (dashboard-flow-ux.md): hide the full "at a glance" card until at least one
  // financial value is entered; show a plain placeholder in its place (no card chrome).
  const hasData = (summary?.completeness.sectionsWithValues ?? 0) >= 1;
  if (!hasData) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="estate-snapshot-placeholder">
        As you fill out financial categories, your estate summary will appear here.
      </p>
    );
  }

  return (
    <Card className="border-l-4" style={{ borderLeftColor: "hsl(168 76% 42%)" }}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          Your estate, at a glance
        </CardTitle>
        <Link href="/estate-summary" className="text-xs text-primary hover:underline flex items-center gap-1">
          View full summary <ArrowRight className="w-3 h-3" />
        </Link>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Estimated estate value</p>
                {/* A negative net estate (insolvent) renders in parenthesized
                    accounting style + destructive color — never bare abs(). */}
                <p className={`text-lg font-bold ${summary!.netEstateValue < 0 ? "text-destructive" : "text-foreground"}`}>
                  {formatApproxNetEstateDisplay(summary!.netEstateValue)}
                </p>
                <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Assets</span>
                    <span>{fmt(summary!.assets.totalValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Liabilities</span>
                    <span>({fmt(summary!.liabilities.totalValue)})</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Monthly cash flow</p>
                <p className={`text-lg font-bold ${summary!.monthlyNet >= 0 ? "text-green-600" : "text-destructive"}`}>
                  {summary!.monthlyNet >= 0 ? "+" : "-"}{fmt(summary!.monthlyNet)}/mo
                </p>
                <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Income</span>
                    <span>{fmt(summary!.monthlyIncome.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Expenses</span>
                    <span>({fmt(summary!.monthlyExpenses.total)})</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t flex justify-between text-xs text-muted-foreground">
              <span>Probate value: <strong className="text-foreground">{fmt(summary!.assets.probateValue)}</strong></span>
              <span>Est. EAT: <strong className="text-foreground">{fmt(summary!.eat.taxAmount)}</strong></span>
            </div>
            {summary!.completeness.sectionsWithValues < summary!.completeness.totalAssetSections && (
              <p className="text-xs text-muted-foreground">
                Based on {summary!.completeness.sectionsWithValues} of {summary!.completeness.totalAssetSections} asset categories
              </p>
            )}
        </div>
      </CardContent>
    </Card>
  );
}
